/* Hallmark · component: device keys · existing Cobalt/Mantine tokens
 * pre-emit critique: P4 H4 E4 S5 R5 V4
 * states: default · hover · focus · active · disabled · loading · error · success
 */
import { ActionIcon, Alert, Button, Divider, Group, Loader, Select, Stack, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ApiError, get, post } from './api';

const protocols = [
  { value: 'awg31', label: 'AWG 3.1' },
  { value: 'awg2', label: 'AWG 2' },
  { value: 'happ-direct', label: 'VLESS' },
  { value: 'hysteria2', label: 'Hysteria2' },
  { value: 'trojan', label: 'Trojan' },
  { value: 'naiveproxy', label: 'NaïveProxy' },
  { value: 'mieru', label: 'Mieru' },
];
type DeviceKey = { id: string; kind: string; label: string; forDevice: boolean; status: string; secretAvailable: boolean };
const protocolName = (kind: string) => protocols.find(item => item.value === kind)?.label ?? kind;
function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'Сессия завершилась. Войдите в аккаунт снова.';
    if (error.status === 403) return 'Для выдачи ключей нужен действующий доступ к VPN.';
    if (error.status === 409) return 'Ключ недоступен. Обновите список.';
  }
  return 'Не удалось выполнить запрос. Проверьте связь и повторите.';
}

function KeyIcon({ action }: { action: 'copy' | 'refresh' | 'check' }) {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {action === 'copy' ? <><rect x="8" y="8" width="12" height="13" rx="2" /><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" /></> :
      action === 'refresh' ? <><path d="M20 7v5h-5M4 17v-5h5" /><path d="M6.1 6.1A8 8 0 0 1 20 12M4 12a8 8 0 0 0 13.9 5.9" /></> : <path d="m5 12 4 4L19 6" />}
  </svg>;
}

export function DeviceKeys({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const keys = useQuery({ queryKey: ['deviceKeys', accountId], gcTime: 0, retry: false,
    queryFn: async () => (await get<{ items: DeviceKey[] }>('/api/me/keys')).items.filter(key => key.forDevice && key.status === 'active' && key.secretAvailable) });
  const [name, setName] = useState('');
  const [kind, setKind] = useState('awg31');
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copyingId, setCopyingId] = useState('');
  const [copiedId, setCopiedId] = useState('');
  useEffect(() => { const timer = window.setTimeout(() => setCopiedId(''), 2000); return () => window.clearTimeout(timer); }, [copiedId]);
  const validName = new TextEncoder().encode(name.trim()).length;

  async function run(action: () => Promise<void>) {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError(''); setSuccess('');
    try { await action(); } catch (failure) { setError(errorMessage(failure)); }
    finally { busyRef.current = false; setBusy(false); }
  }
  async function create() {
    setAttempted(true);
    const { key } = await post<{ key: DeviceKey }>('/api/me/keys', { label: name.trim(), kind, forDevice: true }, requestId);
    setAttempted(false); setRequestId(crypto.randomUUID()); setName('');
    queryClient.setQueryData<DeviceKey[]>(['deviceKeys', accountId], previous => [key, ...(previous ?? []).filter(item => item.id !== key.id)]);
    void keys.refetch();
    setSuccess('Устройство добавлено');
  }
  async function copy(key: DeviceKey) {
    setCopyingId(key.id); setCopiedId('');
    try {
      // The profile goes directly to the clipboard, never into UI state or query/storage.
      const result = await post<{ secret: string }>(`/api/me/keys/${encodeURIComponent(key.id)}/reveal`, {});
      try { await navigator.clipboard.writeText(result.secret); }
      catch { setError('Не удалось скопировать ключ. Повторите копирование.'); return; }
      setCopiedId(key.id); setSuccess('Ключ скопирован');
    } finally { setCopyingId(''); }
  }

  return <Stack gap="md" component="section" aria-label="Ключи для других устройств">
    <Group justify="space-between" wrap="nowrap">
      <Title order={2} style={{ minWidth: 0 }}>Другие устройства</Title>
      <Tooltip label="Обновить" openDelay={800}>
        <ActionIcon size={44} variant="subtle" aria-label="Обновить" loading={keys.isFetching} disabled={busy} onClick={() => void keys.refetch()}><KeyIcon action="refresh" /></ActionIcon>
      </Tooltip>
    </Group>
    <form onSubmit={event => { event.preventDefault(); if (validName > 0 && validName <= 128) void run(create); }}>
      <Stack gap="sm">
        <TextInput label="Имя устройства" value={name} onChange={event => setName(event.currentTarget.value)} disabled={busy || attempted}
          error={validName > 128 ? 'Имя слишком длинное' : undefined} required />
        <Select label="Протокол" data={protocols} value={kind} onChange={value => { if (value) setKind(value); }} disabled={busy || attempted} allowDeselect={false} />
        <Button type="submit" loading={busy && !copyingId} disabled={busy || validName === 0 || validName > 128}>{attempted ? 'Повторить генерацию' : 'Сгенерировать'}</Button>
        {attempted && <Button variant="subtle" disabled={busy} onClick={() => { setAttempted(false); setRequestId(crypto.randomUUID()); setName(''); setError(''); }}>Новый ключ</Button>}
      </Stack>
    </form>
    {error && <Alert color="red" role="alert">{error}</Alert>}
    {success && <Text role="status" c="var(--color-success)">{success}</Text>}
    <Divider />
    {keys.isPending ? <Loader size="sm" aria-label="Загружаем ключи" /> : keys.isError ? <Alert color="red">{errorMessage(keys.error)}</Alert> : keys.data.length === 0 ? <Text c="dimmed">Ключей пока нет.</Text> :
      <Stack gap={0}>{keys.data.map((key, index) => <Stack gap={0} key={key.id}>
        {index > 0 && <Divider />}
        <Group justify="space-between" wrap="nowrap" py="sm" gap="md">
          <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
            <Text fw={600} style={{ overflowWrap: 'anywhere' }}>{key.label}</Text>
            <Text size="sm" c="dimmed">{protocolName(key.kind)}</Text>
          </Stack>
          <Tooltip label={copiedId === key.id ? 'Скопировано' : 'Копировать'} openDelay={800}>
            <ActionIcon size={44} variant="subtle" aria-label={`Копировать ключ: ${key.label}`} loading={copyingId === key.id} disabled={busy} onClick={() => void run(() => copy(key))}>
              <KeyIcon action={copiedId === key.id ? 'check' : 'copy'} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Stack>)}</Stack>}
  </Stack>;
}
